import {
  LAYOUT_COMBINED_NOTE,
  formatStageBspPercentLabel,
  RECEIPT_PLC_OPTIONS,
  PLC_HALF_RATE_NOTE,
  PLC_PENDING_LABEL,
  formatInventoryType,
  formatPlotNumber,
  formatTentativeSize,
  getApplicantBlocks,
  normalizeInventoryCount,
  sumPaymentPlan,
} from "../adminUtils";
import { buildReceiptTermsIntro, RECEIPT_TERMS } from "../receiptTerms";
import { formatCurrency, formatDate } from "../utils";

function ReceiptBrand() {
  return (
    <div className="receipt-brand">
      <p className="receipt-brand__name">SQUAREONE</p>
      <p className="receipt-brand__divider">—ESTATES—</p>
    </div>
  );
}

function ReceiptRow({ label, value }) {
  return (
    <div className="receipt-row">
      <span className="receipt-row__label">{label}</span>
      <span className="receipt-row__value">{value || "—"}</span>
    </div>
  );
}

function ApplicantCard({ applicant }) {
  return (
    <div className="receipt-card">
      <h3 className="receipt-card__title">{applicant.title} Details</h3>
      <ReceiptRow label="Name" value={applicant.name} />
      {applicant.userId && <ReceiptRow label="User ID" value={applicant.userId} />}
      <ReceiptRow label="Email ID" value={applicant.email} />
      <ReceiptRow label="Address" value={applicant.address} />
      <ReceiptRow label="Contact No." value={applicant.mobile} />
      <ReceiptRow label="Aadhaar No." value={applicant.aadhaar} />
      <ReceiptRow label="PAN No." value={applicant.pan} />
    </div>
  );
}

export default function CustomerReceipt({ customer }) {
  const paidPercent = customer.totalAmount
    ? Math.round((customer.paidAmount / customer.totalAmount) * 100)
    : 0;
  const tokenDisplay = customer.tokenAmount;
  const applicants = getApplicantBlocks(customer);

  return (
    <div className="portal-page receipt-page">
      <header className="receipt-header">
        <ReceiptBrand />
      </header>

      <section className="portal-hero portal-hero--compact receipt-hero">
        <div className="portal-hero__inner">
          <p className="receipt-official">Official Receipt</p>
          <p className="portal-eyebrow">Letter of Expression of Interest</p>
          <h1 className="portal-hero__title receipt-hero__title">
            {customer.jointDeal ? `${customer.name} & ${customer.applicant2?.name || "Co-Applicant"}` : customer.name}
          </h1>
          <p className="portal-hero__subtitle">Tentative property details in the upcoming proposed project</p>
        </div>
      </section>

      <section className="portal-section portal-section--compact">
        <div className="portal-section__inner">
          {applicants.map((applicant) => (
            <ApplicantCard key={applicant.title} applicant={applicant} />
          ))}

          <div className="receipt-card">
            <h3 className="receipt-card__title">Property Details</h3>
            <ReceiptRow
              label="Inventory Type"
              value={formatInventoryType(normalizeInventoryCount(customer.inventoryCount, customer.inventoryType))}
            />
            <ReceiptRow label="Category" value={customer.category || "Residential"} />
            <ReceiptRow label="Plot Number" value={formatPlotNumber(customer.plotNumber, customer.plotPending)} />
            <ReceiptRow label="Tentative Size" value={formatTentativeSize(customer)} />
            <ReceiptRow
              label="Tentative Basic Rate"
              value={customer.basicRatePerSqYd ? `${formatCurrency(customer.basicRatePerSqYd)} per Sq. Yd` : null}
            />
          </div>

          <div className="receipt-card">
            <h3 className="receipt-card__title">Preferred Location Charges (PLC)</h3>
            <p className="receipt-note">{PLC_HALF_RATE_NOTE}</p>
            <div className="receipt-plc-list">
              {RECEIPT_PLC_OPTIONS.map((opt) => {
                const selected = !customer.noPlc && !customer.plcPending && customer.plc?.[opt.id];
                return (
                  <div key={opt.id} className={`receipt-plc-item ${selected ? "receipt-plc-item--selected" : ""}`}>
                    <span className="receipt-plc-item__mark">{selected ? "✓" : "○"}</span>
                    <span>{opt.label}</span>
                    <span className="receipt-plc-item__rate">{Math.round(opt.rate * 100)}% of BSP</span>
                  </div>
                );
              })}
              {customer.customPlc?.enabled && (
                <div className="receipt-plc-item receipt-plc-item--selected">
                  <span className="receipt-plc-item__mark">✓</span>
                  <span>Custom PLC</span>
                  <span className="receipt-plc-item__rate">
                    {customer.customPlc.mode === "amount"
                      ? formatCurrency(customer.customPlc.amount || 0)
                      : `${customer.customPlc.percent}% of BSP`}
                  </span>
                </div>
              )}
              {customer.plcPending && (
                <div className="receipt-plc-item receipt-plc-item--pending">
                  <span className="receipt-plc-item__mark">✓</span>
                  <span>{PLC_PENDING_LABEL}</span>
                </div>
              )}
              {customer.noPlc && !customer.plcPending && !customer.customPlc?.enabled && (
                <div className="receipt-plc-item">
                  <span className="receipt-plc-item__mark">✓</span>
                  <span>No PLC</span>
                </div>
              )}
            </div>
          </div>

          <div className="portal-stats">
            <div className="portal-stat">
              <span className="portal-stat__label">BSP</span>
              <span className="portal-stat__value">{formatCurrency(customer.bsp ?? customer.totalAmount)}</span>
            </div>
            <div className="portal-stat portal-stat--accent">
              <span className="portal-stat__label">PLC</span>
              <span className="portal-stat__value">
                {customer.plcPending ? "TBD" : formatCurrency(customer.plcAmount || 0)}
              </span>
            </div>
            <div className="portal-stat">
              <span className="portal-stat__label">Grand Total</span>
              <span className="portal-stat__value">
                {customer.plcPending
                  ? `${formatCurrency(customer.bsp ?? 0)} + PLC TBD`
                  : formatCurrency(customer.totalAmount)}
              </span>
            </div>
          </div>

          {customer.paymentPlan?.length > 0 && (
            <div className="receipt-card">
              <h3 className="receipt-card__title">Payment Plan (If Allotted)</h3>
              <div className="admin-table-wrap">
                <table className="admin-table receipt-table">
                  <thead>
                    <tr>
                      <th>Stage</th>
                      {!customer.manualPricing && <th>BSP %</th>}
                      <th>BSP (Rs.)</th>
                      <th>PLC (Rs.)</th>
                      <th>Total (Rs.)</th>
                    </tr>
                  </thead>
                  <tbody>
                    {customer.paymentPlan.flatMap((stage) => {
                      const bspPercentLabel = formatStageBspPercentLabel(stage, { tokenLabel: "Booking" });

                      const rows = [
                        <tr key={stage.id}>
                          <td>{stage.label}</td>
                          {!customer.manualPricing && <td>{bspPercentLabel}</td>}
                          <td>{formatCurrency(stage.bspAmount)}</td>
                          <td>
                            {customer.plcPending && stage.plcPercent > 0
                              ? "TBD"
                              : stage.plcAmount
                                ? formatCurrency(stage.plcAmount)
                                : "—"}
                          </td>
                          <td>
                            <strong>
                              {customer.plcPending && stage.plcPercent > 0
                                ? `${formatCurrency(stage.bspAmount)} + PLC TBD`
                                : formatCurrency(stage.totalAmount)}
                            </strong>
                          </td>
                        </tr>,
                      ];

                      if (stage.id === "layout") {
                        rows.push(
                          <tr key="layout-note">
                            <td colSpan={customer.manualPricing ? 4 : 5}>
                              <div className="receipt-payment-note">
                                <p className="receipt-payment-note__title">
                                  <strong>{LAYOUT_COMBINED_NOTE.title}</strong> {LAYOUT_COMBINED_NOTE.body}
                                </p>
                                <p>{LAYOUT_COMBINED_NOTE.footer}</p>
                              </div>
                            </td>
                          </tr>
                        );
                      }

                      return rows;
                    })}
                    <tr className="admin-table__total-row">
                      <td><strong>Total</strong></td>
                      {!customer.manualPricing && <td />}
                      <td><strong>{formatCurrency(sumPaymentPlan("bspAmount", customer.paymentPlan))}</strong></td>
                      <td>
                        <strong>
                          {customer.plcPending
                            ? "TBD"
                            : formatCurrency(sumPaymentPlan("plcAmount", customer.paymentPlan))}
                        </strong>
                      </td>
                      <td>
                        <strong>
                          {customer.plcPending
                            ? `${formatCurrency(customer.bsp ?? 0)} + PLC TBD`
                            : formatCurrency(sumPaymentPlan("totalAmount", customer.paymentPlan))}
                        </strong>
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
          )}

          <div className="portal-stats">
            <div className="portal-stat portal-stat--accent">
              <span className="portal-stat__label">Amount Paid</span>
              <span className="portal-stat__value">{formatCurrency(customer.paidAmount)}</span>
            </div>
            <div className="portal-stat">
              <span className="portal-stat__label">Balance Due</span>
              <span className="portal-stat__value">{formatCurrency(customer.pendingAmount)}</span>
            </div>
            <div className="portal-stat">
              <span className="portal-stat__label">Progress</span>
              <span className="portal-stat__value">{paidPercent}%</span>
            </div>
          </div>

          <div className="portal-progress">
            <div className="portal-progress__bar">
              <div className="portal-progress__fill" style={{ width: `${paidPercent}%` }} />
            </div>
          </div>

          <div className="receipt-card">
            <h3 className="receipt-card__title">Payment Received</h3>
            {customer.payments?.length > 0 ? (
              <div className="admin-table-wrap">
                <table className="admin-table receipt-table">
                  <thead>
                    <tr>
                      <th>Date</th>
                      <th>Amount</th>
                      <th>Cheque / RTGS No.</th>
                      <th>Bank</th>
                    </tr>
                  </thead>
                  <tbody>
                    {[...customer.payments].reverse().map((payment, idx) => (
                      <tr key={`${payment.date}-${idx}`}>
                        <td>{formatDate(payment.date)}</td>
                        <td>{formatCurrency(payment.amount)}</td>
                        <td>{payment.chequeRtgs || "—"}</td>
                        <td>{payment.bank || "—"}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <p className="portal-muted">No payments recorded yet.</p>
            )}
            <p className="receipt-footer-note">Last updated {formatDate(customer.lastUpdatedAt)}</p>
          </div>

          <div className="receipt-card receipt-terms">
            <h3 className="receipt-card__title">Terms &amp; Conditions</h3>
            <p className="receipt-terms__intro">{buildReceiptTermsIntro(tokenDisplay)}</p>
            <ol className="receipt-terms__list">
              {RECEIPT_TERMS.map((term) => (
                <li key={term.slice(0, 40)}>{term}</li>
              ))}
            </ol>
            <p className="receipt-terms__closing">Yours Sincerely</p>
          </div>

          <div className="receipt-signatures receipt-signatures--compact">
            <div className="receipt-signature receipt-signature--compact">
              <div className="receipt-signature__space" aria-hidden="true" />
              <div className="receipt-signature__line" />
              <p className="receipt-signature__label">Applicant Signature</p>
            </div>
            <div className="receipt-signature receipt-signature--compact">
              <div className="receipt-signature__space" aria-hidden="true" />
              <div className="receipt-signature__line" />
              <p className="receipt-signature__label">Authorized Signatory</p>
            </div>
          </div>
        </div>
      </section>

      <footer className="receipt-footer">
        <ReceiptBrand />
      </footer>
    </div>
  );
}
